/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useMemo } from 'react';
// redux
import { useSelector } from 'react-redux';
import { PRODUCT_COMMENTING, PRODUCT_COMMENTING_V2, PRODUCT_TRENDING_NOW_V2 } from '../../../../state/thunks/account';
// material
import NavItem from './navitem/navitem';
import NavSubItem from './navsubitem/navsubitem';
// styles
import injectSheet from 'react-jss';
import styles from './nav_styles';
import { PRODUCT_ARTICLE_SENTIMENT, PRODUCT_REQUEST_PENDING, PRODUCT_TRENDING_NOW } from '../../../../state/thunks/engagement';
import { url } from '../../../../utils/urls';

/* ========== ~~~~~~~~~~ NAV ~~~~~~~~~~ ========== */
const Nav = injectSheet(styles)((props) => {
	const sidebarOpen = useSelector(state => state.app.sidebarOpen);
  const { mocked, userEmail } = useSelector(state => state.session);
  const isInsticatorUser = mocked || /@insticator.com\s*$/.test(userEmail)
	const currentPage = useSelector(state => state.app.currentPage);
	const isHomePage = currentPage === '/';

  const {
    account: {
      accountProducts: productAccess
   },
    engagement: {
      accountProducts,
      articleSentimentAccessRequest: {
        requestStatus,
      } = {},
      trendingNowAccessRequest,
    }
  } = useSelector((state) => state);

  const hasCommentingAccess = productAccess?.includes(PRODUCT_COMMENTING);
  
  const hasCommentingV2Access = productAccess?.includes(PRODUCT_COMMENTING_V2);

  const hasArticleSentimentAccess = useMemo(() =>
      accountProducts.includes(PRODUCT_ARTICLE_SENTIMENT)
    , [accountProducts]
  )

  const hasTrendingNowAccess = useMemo(() =>
      accountProducts.includes(PRODUCT_TRENDING_NOW)
    , [accountProducts]
  )

  const hasTrendingNowV2Access = useMemo(() =>
    productAccess.includes(PRODUCT_TRENDING_NOW_V2)
  , [productAccess]
)

  const isPendingArticleSentimentAccess = useMemo(() =>
      requestStatus === PRODUCT_REQUEST_PENDING
    , [requestStatus]
  )

  const isPendingTrendingNowAccess = useMemo(() =>
      trendingNowAccessRequest.requestStatus === PRODUCT_REQUEST_PENDING
    , [trendingNowAccessRequest]
  )

  const hasLegacyProductAccess = !hasCommentingV2Access && hasCommentingAccess

  return (
        <nav id="sidebar-nav" className={`${props.classes.nav} sidebarOpen-${sidebarOpen}`}>
            <NavItem icon={`briefcase`} text={`Overview`} route={`/dashboard/overview`} isActive={isHomePage} />
            <NavItem icon={`coins`} text={`Monetization`} route={`/dashboard/monetization`}>
                <NavSubItem text={`Revenue`} route={`/dashboard/monetization/revenue`} />
                <NavSubItem text={`Unit Load RPM`} route={`/dashboard/monetization/unitloadrpm`} />
                <NavSubItem text={`Unit Loads`} route={`/dashboard/monetization/unitloads`} />
            </NavItem>
            {!hasCommentingAccess && <NavItem icon={`toggle-on`} text={`Product Manager`} route={`/dashboard/productmanager`}  />}
            {(hasCommentingAccess || hasCommentingV2Access) &&
              <NavItem icon={`comment-alt`} text={`Commenting`} route={`/dashboard/commenting`} disabled={false}>
                <NavSubItem text={`Analytics`} route={`/dashboard/commenting/analytics`} disabled={false} />
                {hasCommentingAccess && <NavSubItem text={`Get Code`} route={`/dashboard/commenting/code`} disabled={!hasCommentingAccess} />}
                { hasCommentingAccess ?
                    <NavSubItem text={`Configuration`} route={`/dashboard/commenting/configuration`} disabled={!hasCommentingAccess}/>
                    :
                    hasCommentingV2Access ?
                      <NavSubItem text={`Configuration`} route={`/dashboard/commenting/configuration`} disabled={!hasCommentingV2Access}/>
                      :
                      <NavSubItem text={`Configuration`} route={`/dashboard/commenting/requestaccess`} />
                }
                {hasCommentingAccess && <NavSubItem text={`Advanced`} route={`/dashboard/commenting/advanced`} disabled={!hasCommentingAccess} />}
                {isInsticatorUser && hasCommentingV2Access && <NavSubItem text={`Importer Tool`} route={`/dashboard/commenting/importertool`} disabled={!hasCommentingAccess && !hasCommentingV2Access} />}
            </NavItem>
            }
            { hasCommentingV2Access &&
            <NavItem icon={`glasses`} text={`Moderation`} disabled={false}>
              <NavSubItem text={`Dashboard`} externalLink={url.moderationDashboard} disabled={!(hasCommentingV2Access && isInsticatorUser)} />
              <NavSubItem text={`Configuration`} route={`/dashboard/moderation/config`} disabled={!(hasCommentingV2Access && isInsticatorUser)} />
            </NavItem>
            }
            { hasLegacyProductAccess && <NavItem icon={`glasses`} text={`Moderation`} externalLink={url.comment.moderation.url} disabled={!(hasCommentingAccess && isInsticatorUser)} />}            
            <NavItem icon={`poll-h`} text={`Trivia & Polls`} route={`/dashboard/triviapolls`} disabled={false}>
                <NavSubItem text={`Analytics`} route={`/dashboard/triviapolls/analytics`} disabled={false} />
                <NavSubItem text={`Configuration`} route={`/dashboard/triviapolls/configuration`} />
            </NavItem>
            {(hasTrendingNowV2Access) &&
              <NavItem icon={`bolt`} text={`Trending Now`} route={`/dashboard/trendingnow`} disabled={false}>
                <NavSubItem text={`Configuration`} route={`/dashboard/trendingnow/configuration`} />
              </NavItem>
            }
            {hasLegacyProductAccess && 
            <NavItem icon={`puzzle-piece`} text={`Trends & Reactions`} route={`/dashboard/trendsandreactions`} disabled={isPendingArticleSentimentAccess && isPendingTrendingNowAccess}>
                <NavSubItem text={`Analytics`} route={`/dashboard/trendsandreactions/analytics`} disabled={true} />
                <NavSubItem text={`Get Code`} route={`/dashboard/trendsandreactions/getcode`} disabled={!(hasArticleSentimentAccess || hasTrendingNowAccess)} />
                <NavSubItem text={`Configuration`} route={`/dashboard/trendsandreactions/configuration`} disabled={isPendingArticleSentimentAccess && isPendingTrendingNowAccess}/>
                <NavSubItem text={`Advanced`} route={`/dashboard/trendsandreactions/advanced`} disabled={!(mocked && hasArticleSentimentAccess)} />
            </NavItem>
            }
            <NavItem icon={`chart-pie`} text={`Get Data`} route={`/dashboard/getdata`} disabled={true} />
            <NavItem icon={`cog`} text={'Settings'} route={'/dashboard/settings'}>
                <NavSubItem text={'My Account'} route={'/dashboard/settings/account'} />
                <NavSubItem text={'Users'} route={'/dashboard/settings/users'} />
            </NavItem>
        </nav>
	);
});



/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default Nav;
